/**
 * Combobox Utility Functions
 *
 * Helper functions for fuzzy matching, option filtering, and text highlighting.
 *
 * @see docs/planning/combobox-priority-1-component-architecture.md
 */

import type { ComboboxOption, ComboboxOptionGroup } from './combobox.types';

/**
 * Fuzzy matching algorithm without external dependencies
 * Based on Levenshtein distance with optimizations
 *
 * @param text - Text to search in
 * @param query - Search query
 * @param threshold - Match threshold (0-1), default 0.6
 * @returns boolean - True if match found
 */
export function fuzzyMatch(text: string, query: string, threshold: number = 0.6): boolean {
  if (!query) return true;
  if (!text) return false;

  // Exact match
  if (text === query) return true;

  // Simple contains check first (fast path)
  if (text.includes(query)) return true;

  // Calculate Levenshtein distance
  const distance = levenshteinDistance(text, query);
  const maxLength = Math.max(text.length, query.length);
  const similarity = 1 - distance / maxLength;

  return similarity >= threshold;
}

/**
 * Levenshtein distance calculation (edit distance)
 * Optimized with single-row algorithm
 */
function levenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[] = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i++) {
    let prev = i;

    for (let j = 1; j <= b.length; j++) {
      const current = matrix[j];

      if (a[i - 1] === b[j - 1]) {
        matrix[j] = matrix[j - 1];
      } else {
        matrix[j] = Math.min(
          matrix[j - 1] + 1, // insertion
          prev + 1, // deletion
          matrix[j] + 1 // substitution
        );
      }

      matrix[j - 1] = prev;
      prev = current;
    }

    matrix[b.length] = prev;
  }

  return matrix[b.length];
}

/**
 * Filter options based on search query
 */
export function filterOptions<TValue = string>(
  options: ComboboxOption<TValue>[],
  query: string,
  config: {
    caseSensitive?: boolean;
    searchDescription?: boolean;
    fuzzyThreshold?: number;
  } = {}
): ComboboxOption<TValue>[] {
  if (!query) return options;

  const { caseSensitive = false, searchDescription = false, fuzzyThreshold = 0.6 } = config;

  return options.filter((option) => {
    const searchText = searchDescription
      ? `${option.label} ${option.description || ''}`
      : option.label;

    const text = caseSensitive ? searchText : searchText.toLowerCase();
    const searchQuery = caseSensitive ? query : query.toLowerCase();

    return fuzzyMatch(text, searchQuery, fuzzyThreshold);
  });
}

/**
 * Flatten grouped options to a single array
 */
export function flattenOptions<TValue = string>(
  options: ComboboxOption<TValue>[] | ComboboxOptionGroup<TValue>[]
): ComboboxOption<TValue>[] {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  // Check if first item is a group
  const firstItem = options[0];
  if ('options' in firstItem && Array.isArray(firstItem.options)) {
    // It's grouped options
    return (options as ComboboxOptionGroup<TValue>[]).flatMap((group) => group.options);
  }

  // It's already flat
  return options as ComboboxOption<TValue>[];
}

/**
 * Get selected options from values
 */
export function getSelectedOptions<TValue = string>(
  value: TValue | TValue[] | undefined,
  options: ComboboxOption<TValue>[]
): ComboboxOption<TValue>[] {
  if (!value) return [];

  const flatOptions = flattenOptions(options);
  const values = Array.isArray(value) ? value : [value];

  return flatOptions.filter((opt) => values.includes(opt.value));
}

/**
 * Check if option is selected
 */
export function isOptionSelected<TValue = string>(
  option: ComboboxOption<TValue>,
  value: TValue | TValue[] | undefined
): boolean {
  if (!value) return false;

  const values = Array.isArray(value) ? value : [value];
  return values.includes(option.value);
}

/**
 * Get option by value
 */
export function getOptionByValue<TValue = string>(
  value: TValue,
  options: ComboboxOption<TValue>[]
): ComboboxOption<TValue> | null {
  const flatOptions = flattenOptions(options);
  return flatOptions.find((opt) => opt.value === value) || null;
}

/**
 * Group options by group property
 */
export function groupOptions<TValue = string>(
  options: ComboboxOption<TValue>[]
): ComboboxOptionGroup<TValue>[] {
  const grouped = new Map<string, ComboboxOption<TValue>[]>();

  options.forEach((option) => {
    const groupKey = option.group || 'Other';
    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, []);
    }
    grouped.get(groupKey)!.push(option);
  });

  return Array.from(grouped.entries()).map(([label, opts]) => ({
    label,
    options: opts,
  }));
}

/**
 * Check if options are grouped
 */
export function isGroupedOptions<TValue = string>(
  options: ComboboxOption<TValue>[] | ComboboxOptionGroup<TValue>[]
): options is ComboboxOptionGroup<TValue>[] {
  if (!Array.isArray(options) || options.length === 0) {
    return false;
  }

  const firstItem = options[0];
  return 'options' in firstItem && Array.isArray(firstItem.options);
}

/**
 * Normalize search config
 */
export function normalizeSearchConfig(search: boolean | object | undefined): {
  enabled: boolean;
  [key: string]: unknown;
} {
  if (typeof search === 'boolean') {
    return { enabled: search };
  }
  if (typeof search === 'object' && search !== null) {
    return { enabled: true, ...search };
  }
  return { enabled: false };
}

/**
 * Normalize virtual config
 */
export function normalizeVirtualConfig(
  virtual: boolean | object | undefined,
  optionsCount: number
): { enabled: boolean; threshold?: number; [key: string]: unknown } {
  const threshold = 50; // Auto-enable for 50+ items

  if (typeof virtual === 'boolean') {
    return { enabled: virtual, threshold };
  }
  if (typeof virtual === 'object' && virtual !== null) {
    return { enabled: true, threshold, ...virtual };
  }
  // Auto-enable if options > threshold
  return { enabled: optionsCount >= threshold, threshold };
}

/**
 * Generate unique ID for combobox elements
 */
let idCounter = 0;
export function generateId(prefix: string = 'combobox'): string {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: never[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function debounced(...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  };
}

/**
 * Clamp value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Check if element is visible in container
 */
export function isElementVisible(element: HTMLElement, container: HTMLElement): boolean {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;
}

/**
 * Scroll element into view if not visible
 */
export function scrollIntoViewIfNeeded(element: HTMLElement, container: HTMLElement): void {
  if (!isElementVisible(element, container)) {
    element.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }
}
